/**
 * Control-probe hooks (P282.3) -- define implementation probes on a
 * control (from the shared catalogue), delete them, and derive Test of
 * Implementation from their live statuses.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import type {
  ControlProbe,
  ControlProbeCreateIn,
  ControlProbeList,
  TestImplementationResult,
} from "shared/api/types";

export const probeKeys = {
  list: (controlId: string) => ["control-probes", controlId] as const,
};

export function useControlProbes(
  controlId: string,
): UseQueryResult<ControlProbeList, ApiError> {
  return useQuery({
    queryKey: probeKeys.list(controlId),
    queryFn: () =>
      api.get<ControlProbeList>(`/v1/framework/controls/${controlId}/probes`),
    enabled: Boolean(controlId),
  });
}

export function useCreateProbe(
  controlId: string,
): UseMutationResult<ControlProbe, ApiError, ControlProbeCreateIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api.post<ControlProbe>(`/v1/framework/controls/${controlId}/probes`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: probeKeys.list(controlId) }),
  });
}

export function useDeleteProbe(
  controlId: string,
): UseMutationResult<void, ApiError, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (probeId) => api.delete<void>(`/v1/framework/probes/${probeId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: probeKeys.list(controlId) }),
  });
}

export function useDeriveToI(
  cycleId: string,
  controlId: string,
): UseMutationResult<TestImplementationResult, ApiError, void> {
  const qc = useQueryClient();
  return useMutation({
    // E1 (round 8) -- this mutation's caller awaits `mutateAsync` and
    // catches, which react-query cannot see. Without this the global
    // failure sink announces the server's detail as well, so one
    // failure reaches the reader twice in two wordings.
    meta: { handled: true },
    mutationFn: () =>
      api.post<TestImplementationResult>(
        `/v1/framework/cycles/${cycleId}/controls/${controlId}/test-implementation`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["control-tests", cycleId, controlId] });
      void qc.invalidateQueries({ queryKey: ["framework-assessments"] });
    },
  });
}
